// ShareDB is pretty good at handling actual errors, but not great at
// handling recoverable errors, like invalid or expired tokens. In
// order to give an editor client the hooks they need to recover from
// errors, we need to be able to intercept them before ShareDB can get
// ahold of them. The best place to do this is in `connection`'s
// `handleMessage`, so we'll monkey-patch that here.
//
// @param connection a ShareDB Connection object
// @param onError an error handler. This handler should immediately
//        return true if it can recover from the error, and is passed
//        a callback it should call once it's successfully recovered
//        from the error.
export default function handleErrors(connection, errorHandlers) {
  const oldHandleMessage = connection.handleMessage.bind(connection);
  const findHandler = (collection, id) => {
    if (errorHandlers[collection]) {
      return errorHandlers[collection][id];
    }
  };

  const handleSubscribeError = (error, collection, documentId) => {
    const doc = connection.getExisting(collection, documentId);
    const onError = findHandler(collection, documentId);

    // If we failed on a 'subscribe' operation, we want to send
    // all of our pending operations and resubscribe once we've
    // recovered.
    return (
      onError &&
      onError(error, () => {
        doc.flush();
        doc._resubscribe();
      })
    );
  };

  connection.handleMessage = function (message) {
    if (message.error) {
      const err = new Error(message.error.message);
      err.code = message.error.code;
      err.data = message;

      if (message.a === 'op') {
        const doc = this.getExisting(message.c, message.d);
        const onError = findHandler(message.c, message.d);

        // If we failed on an 'op' operation, we want to immediately
        // disconnect from the document, and reconnect once we've
        // recovered.
        if (
          onError &&
          onError(err, () => {
            if (doc.inflightOp) {
              doc.pendingOps.unshift(doc.inflightOp);
              doc.inflightOp = null;
            }
            doc.flush();
            doc.subscribe();
          })
        ) {
          doc.unsubscribe();
          return;
        }
      } else if (message.a === 's') {
        if (handleSubscribeError(err, message.c, message.d)) return;
      } else if (message.a === 'bs') {
        const handled = [];

        if (message.data) {
          for (const id in message.data) {
            handled.push(handleSubscribeError(err, message.c, id));
          }
        } else if (Array.isArray(message.b)) {
          message.b.forEach((id) => {
            handled.push(handleSubscribeError(err, message.c, id));
          });
        } else if (message.b) {
          for (const id in message.b) {
            handled.push(handleSubscribeError(err, message.c, id));
          }
        }

        if (handled.length > 0 && handled.every((e) => e)) return;
      }
      console.error(
        `Unhandled collaboration error during action '${err.data.a}': (${err.code}) ${err.message}`
      );
    }

    // If we can't recover from the error, defer to the original
    // handleMessage.
    oldHandleMessage(message);
  };
}
